/**
 * Formats a date string to DD-MM-YYYY format
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} format - Optional format string (e.g., 'MMM yyyy')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = 'DD-MM-YYYY') => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  if (format === 'MMM yyyy') {
    return date
      .toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      })
      .toUpperCase();
  }

  if (format === 'DD MM') {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    return `${day} ${month}`;
  }

  if (format === 'DD MMM') {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month}`;
  }

  if (format === 'DD MMM YYYY') {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  if (format === 'DD MM YYYY') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  if (format === 'DD MMM YYYY') {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  if (format === 'MMM DD, YYYY') {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  }

  if (format === 'DD/MM/YYYY') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  if (format === 'DD-MMM-YYYY') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = String(date.getFullYear());
    return `${day}-${month}-${year}`;
  }

  /** e.g. 5th March, 2025 */
  if (format === 'Do MMMM, YYYY') {
    const d = date.getDate();
    const j = d % 10;
    const k = d % 100;
    const suffix =
      j === 1 && k !== 11
        ? 'st'
        : j === 2 && k !== 12
          ? 'nd'
          : j === 3 && k !== 13
            ? 'rd'
            : 'th';
    const month = date.toLocaleString('en-GB', { month: 'long' });
    return `${d}${suffix} ${month}, ${date.getFullYear()}`;
  }

  /** ISO calendar date YYYY-MM-DD */
  if (format === 'YYYY-MM-DD') {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
  }

  /** US short date e.g. 2/7/2025 */
  if (format === 'M/D/YYYY') {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  /** e.g. Jul/06/2026 06:53 PM */
  if (format === 'MMM/DD/YYYY hh:mm A') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const meridiem = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${month}/${day}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${meridiem}`;
  }

  /** e.g. 31/08/2026 06:53 PM */
  if (format === 'DD/MM/YYYY hh:mm A') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const meridiem = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${meridiem}`;
  }

  if (format === 'DD-MMM-YY') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};
/**
 * Formats a date string to a more readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatReadableDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};
/**
 * Formats a date string to relative time (e.g., "2 hours ago")
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatRelativeTime = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000)
    return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

// Inclusive range check on the `YYYY-MM-DD` prefix (same comparison style the
// API/date pickers use). A missing bound means that side is unconstrained —
// used to validate a Due Date against its linked Sprint's or Project's range.
export const isDateWithinRange = (date, start, end) => {
  if (!date) return true;
  const d = String(date).slice(0, 10);
  if (start && d < String(start).slice(0, 10)) return false;
  if (end && d > String(end).slice(0, 10)) return false;
  return true;
};
/**
 * Truncates text to a specified length and adds ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};
/**
 * Capitalizes the first letter of each word in a string
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = text => {
  if (!text) return '';
  return text.replace(/\b\w/g, char => char.toUpperCase());
};
/**
 * Formats a file size in bytes to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = bytes => {
  // Handle null, undefined, or invalid values
  if (bytes === null || bytes === undefined || isNaN(bytes) || bytes < 0) {
    return 'Unknown size';
  }
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Rounds hour values to 2 decimals to strip floating-point noise from sums.
export const roundHours = value => Math.round((Number(value) || 0) * 100) / 100;

/**
 * Extracts display name from API response fields that can be string or { id, name } object.
 * Used for employeeType, designation, department, workLocation from users API.
 * @param {string|object} field - Field value (string or { id, name } object)
 * @returns {string} Display name or empty string
 */
export const getDisplayName = field => {
  if (field == null || field === '') return '';
  if (typeof field === 'object' && field?.name != null)
    return String(field.name);
  return String(field);
};

/**
 * Extracts id for Select/dropdown value from fields that can be primitive or { id, name } object.
 * Prevents "Objects are not valid as React child" when formData has API object.
 * @param {string|number|object} field - Field value (id, or { id, name } object)
 * @returns {string|number} Id for Select value, or empty string
 */
export const getSelectValue = field => {
  if (field == null || field === '') return '';
  if (typeof field === 'object' && field !== null && 'id' in field)
    return field.id ?? '';
  return field;
};

export const formatRoleName = role => {
  if (role == null) return '';

  const name = typeof role === 'string' ? role : (role?.roleName ?? '');
  if (!name) return '';

  return name
    .split('_')
    .map(word =>
      word.length <= 2
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
};
